// @givmo/board — a committee's own acts from its "⋯" menu (CBG-YR6-REG, `board-committees.md`
// §4.2–§4.4). No frame drew these dialogs.
//
//   * Change its details: its name, kind, establishment day and where that is recorded, sending only
//     what changed. A rename keeps its charter's label: the charter's versions stay one chain.
//   * Link a charter already recorded: a committee-charter chain in the governing documents that no
//     other committee holds. Board reads no charter to decide anything; re-pointing is the cure for a
//     wrong link.
//   * Dissolve it: the day, where it is recorded and a note. Every membership ends with it; nothing
//     recorded is deleted.
//   * Correct a dissolution made by mistake, saying why: the committee stands again, and the
//     memberships its dissolution ended stand again for each member still on the board.
//
// Each is the Owner's act (`board_member:manage`); dissolving and correcting change who serves, so a
// person makes them (agent-never item 8).

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCommitteeContinuity,
  type BoardCommitteeOut,
  type BoardCommitteeRecordedIn,
  type OrgBoardGoverningDocumentOut,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  ErrorMessage,
  RadioTile,
  RadioTileGroup,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { todayAsDateInput } from '../../lib/display';
import { extractCommittees } from './committees-decode';
import { RecordedInPicker } from './recorded-in-picker';
import { committeesWords as w } from './committees-words';

function DialogFrame({
  title,
  testId,
  pending,
  onClose,
  children,
}: {
  title: string;
  testId: string;
  pending: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={title}
        className="flex flex-col gap-16 overflow-y-auto"
        testId={testId}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function Footer({
  pending,
  canSave,
  saveLabel,
  danger,
  onClose,
  onSave,
  testIdPrefix,
}: {
  pending: boolean;
  canSave: boolean;
  saveLabel: string;
  danger?: boolean;
  onClose: () => void;
  onSave: () => void;
  testIdPrefix: string;
}) {
  return (
    <div className="flex justify-end gap-10">
      <Button
        appearance="saas"
        tone="secondary"
        disabled={pending}
        onClick={onClose}
        testId={`${testIdPrefix}-cancel`}
      >
        {w.cancel}
      </Button>
      <Button
        appearance="saas"
        tone={danger === true ? 'danger' : 'primary'}
        loading={pending}
        disabled={pending || !canSave}
        onClick={onSave}
        testId={`${testIdPrefix}-save`}
      >
        {saveLabel}
      </Button>
    </div>
  );
}

/** The establishment as stored, in the picker's terms (a source this reader can't open reads as none). */
function storedEstablishment(committee: BoardCommitteeOut): BoardCommitteeRecordedIn | null {
  if (committee.established_source_kind === 'entered') return null;
  const source = committee.established_source;
  return source ? { kind: committee.established_source_kind, id: source.id } : null;
}

/** A change's body: only what differs from the stored committee. */
export function committeeChanges(
  committee: BoardCommitteeOut,
  next: {
    name: string;
    continuity: BoardCommitteeContinuity;
    established: string;
    recordedIn: BoardCommitteeRecordedIn | null;
  },
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  if (next.name.trim() !== committee.name) changes.name = next.name.trim();
  if (next.continuity !== committee.continuity) changes.continuity = next.continuity;
  const establishedOrNull = next.established.length > 0 ? next.established : null;
  if (establishedOrNull !== (committee.established_on ?? null)) {
    changes.established_on = establishedOrNull;
  }
  const hidden = committee.established_source_kind !== 'entered' && !committee.established_source;
  const stored = storedEstablishment(committee);
  const before = stored === null ? null : `${stored.kind}:${stored.id}`;
  const after = next.recordedIn === null ? null : `${next.recordedIn.kind}:${next.recordedIn.id}`;
  if (after !== before && !(hidden && after === null)) changes.recorded_in = next.recordedIn;
  return changes;
}

export function CommitteeDetailsDialog({
  orgRef,
  committee,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const [name, setName] = useState(committee.name);
  const [continuity, setContinuity] = useState<BoardCommitteeContinuity>(committee.continuity);
  const [established, setEstablished] = useState(committee.established_on ?? '');
  const [recordedIn, setRecordedIn] = useState(storedEstablishment(committee));
  const [error, setError] = useState<string | null>(null);
  const changes = committeeChanges(committee, { name, continuity, established, recordedIn });
  const save = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerUpdateBoardCommittee({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: changes,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.changed);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.changeFailed),
  });
  return (
    <DialogFrame
      title={w.changeTitle(committee.name)}
      testId="committee-details-dialog"
      pending={save.isPending}
      onClose={onClose}
    >
      <TextField
        label={w.nameLabel}
        value={name}
        maxLength={120}
        disabled={save.isPending}
        onChange={(event) => setName(event.target.value)}
        testId="committee-details-name"
      />
      <RadioTileGroup
        value={continuity}
        onValueChange={(next) => setContinuity(next === 'ad_hoc' ? 'ad_hoc' : 'standing')}
        aria-label={w.kindLabel}
        className="grid grid-cols-2 gap-10 max-sm:grid-cols-1"
        testId="committee-details-kind"
      >
        <RadioTile value="standing" label={w.standing} description={w.standingNote} />
        <RadioTile value="ad_hoc" label={w.adHoc} description={w.adHocNote} />
      </RadioTileGroup>
      <TextField
        label={w.establishedLabel}
        type="date"
        value={established}
        disabled={save.isPending}
        onChange={(event) => setEstablished(event.target.value)}
        testId="committee-details-established"
      />
      <RecordedInPicker
        orgRef={orgRef}
        value={recordedIn}
        onChange={setRecordedIn}
        disabled={save.isPending}
        testId="committee-details-recorded-in"
      />
      {error !== null ? (
        <ErrorMessage testId="committee-details-error">{error}</ErrorMessage>
      ) : null}
      <Footer
        pending={save.isPending}
        canSave={name.trim().length > 0 && Object.keys(changes).length > 0}
        saveLabel={w.saveChanges}
        onClose={onClose}
        onSave={() => {
          setError(null);
          save.mutate();
        }}
        testIdPrefix="committee-details"
      />
    </DialogFrame>
  );
}

export function CharterLinkDialog({
  orgRef,
  committee,
  charters,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  /** The committee-charter chains recorded in the governing documents. */
  charters: readonly OrgBoardGoverningDocumentOut[];
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const committeesQuery = useQuery({
    ...operations.officerListBoardCommitteesOptions({ path: { org_ref: orgRef } }),
    select: extractCommittees,
  });
  const held = useMemo(
    () =>
      new Set(
        (committeesQuery.data ?? [])
          .filter((row) => row.id !== committee.id && row.charter_label)
          .map((row) => row.charter_label ?? ''),
      ),
    [committeesQuery.data, committee.id],
  );
  const options = charters
    .map((chain) => chain.label ?? '')
    .filter((label) => label.length > 0 && !held.has(label) && label !== committee.charter_label)
    .map((label) => ({ value: label, label }));
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const link = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerLinkBoardCommitteeCharter({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: { label },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.linked);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.linkFailed),
  });
  return (
    <DialogFrame
      title={w.linkTitle}
      testId="committee-link-dialog"
      pending={link.isPending}
      onClose={onClose}
    >
      {committeesQuery.isSuccess && options.length === 0 ? (
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {w.linkNone}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          <Select
            label={w.linkLabel}
            appearance="saas"
            value={label}
            disabled={link.isPending || committeesQuery.isPending}
            onValueChange={(next) => setLabel(next ?? '')}
            options={options}
            testId="committee-link-label"
          />
          <FieldNote>{w.linkNote}</FieldNote>
        </div>
      )}
      {error !== null ? <ErrorMessage testId="committee-link-error">{error}</ErrorMessage> : null}
      <Footer
        pending={link.isPending}
        canSave={label.length > 0}
        saveLabel={w.linkSave}
        onClose={onClose}
        onSave={() => {
          setError(null);
          link.mutate();
        }}
        testIdPrefix="committee-link"
      />
    </DialogFrame>
  );
}

export function DissolveDialog({
  orgRef,
  committee,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const [day, setDay] = useState(todayAsDateInput());
  const [recordedIn, setRecordedIn] = useState<BoardCommitteeRecordedIn | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const dissolve = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerDissolveBoardCommittee({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: {
          dissolved_on: day,
          ...(recordedIn !== null ? { recorded_in: recordedIn } : {}),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.dissolved(committee.name));
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.dissolveFailed),
  });
  return (
    <DialogFrame
      title={w.dissolveTitle(committee.name)}
      testId="committee-dissolve-dialog"
      pending={dissolve.isPending}
      onClose={onClose}
    >
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {w.dissolveBody}
      </p>
      <TextField
        label={w.dissolvedOnLabel}
        type="date"
        value={day}
        disabled={dissolve.isPending}
        onChange={(event) => setDay(event.target.value)}
        testId="committee-dissolve-day"
      />
      <RecordedInPicker
        orgRef={orgRef}
        value={recordedIn}
        onChange={setRecordedIn}
        disabled={dissolve.isPending}
        testId="committee-dissolve-recorded-in"
      />
      <Textarea
        label={w.noteLabel}
        value={note}
        maxLength={500}
        rows={2}
        disabled={dissolve.isPending}
        onChange={(event) => setNote(event.target.value)}
        testId="committee-dissolve-note"
      />
      {error !== null ? (
        <ErrorMessage testId="committee-dissolve-error">{error}</ErrorMessage>
      ) : null}
      <Footer
        pending={dissolve.isPending}
        canSave={day.length > 0}
        saveLabel={w.dissolveSave}
        danger
        onClose={onClose}
        onSave={() => {
          setError(null);
          dissolve.mutate();
        }}
        testIdPrefix="committee-dissolve"
      />
    </DialogFrame>
  );
}

export function CorrectDissolutionDialog({
  orgRef,
  committee,
  onClose,
  onDone,
}: {
  orgRef: string;
  committee: BoardCommitteeOut;
  onClose: () => void;
  onDone: (updated: BoardCommitteeOut) => void;
}) {
  const operations = useBoardOperations();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const correct = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerCorrectBoardCommitteeDissolution({
        path: { org_ref: orgRef, committee_id: committee.id },
        body: { note: note.trim() },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (updated) => {
      toast.success(w.corrected);
      onDone(updated);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.correctFailed),
  });
  return (
    <DialogFrame
      title={w.correctTitle(committee.name)}
      testId="committee-correct-dialog"
      pending={correct.isPending}
      onClose={onClose}
    >
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {w.correctBody}
      </p>
      <div className="flex flex-col gap-6">
        <Textarea
          label={w.correctNoteLabel}
          value={note}
          maxLength={500}
          rows={3}
          disabled={correct.isPending}
          onChange={(event) => setNote(event.target.value)}
          testId="committee-correct-note"
        />
        <FieldNote>{w.correctNoteNote}</FieldNote>
      </div>
      {error !== null ? (
        <ErrorMessage testId="committee-correct-error">{error}</ErrorMessage>
      ) : null}
      <Footer
        pending={correct.isPending}
        canSave={note.trim().length > 0}
        saveLabel={w.correctSave}
        onClose={onClose}
        onSave={() => {
          setError(null);
          correct.mutate();
        }}
        testIdPrefix="committee-correct"
      />
    </DialogFrame>
  );
}
